import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, CheckCircle2, RotateCcw, AlertTriangle,
  Clock, ListChecks, ClipboardList,
} from "lucide-react";
import { authHeaders, API, formatApiErrorDetail } from "./api";

const PRIORITIES = ["Low", "Medium", "High"];
const TYPES = ["General", "Documents", "Signature", "Lender"];

const fmtDMY = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

const priorityStyle = (p) =>
  p === "High" ? "bg-red-50 text-red-700 border-red-200"
    : p === "Low" ? "bg-cream-3 text-forest/70 border-forest/15"
    : "bg-amber-50 text-amber-800 border-amber-200";

const typeStyle = (t) =>
  t === "Documents" ? "bg-sky-50 text-sky-800 border-sky-200"
  : t === "Signature" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
  : t === "Lender" ? "bg-indigo-50 text-indigo-700 border-indigo-200"
  : "bg-cream-3 text-forest/70 border-forest/15";

const StatBox = ({ label, count, icon: Icon, tone }) => {
  const toneCls = {
    gold: "border-gold/40 bg-gold-soft/15 text-gold-dark",
    forest: "border-forest/15 bg-cream-3 text-forest/70",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    red: "border-red-200 bg-red-50 text-red-700",
  }[tone];
  return (
    <div
      data-testid={`tasks-stat-${label.toLowerCase().replace(" ", "-")}`}
      className={`border rounded-lg p-3 ${toneCls}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold opacity-80">{label}</span>
        <Icon size={14} strokeWidth={1.8} />
      </div>
      <p className="font-serif text-2xl mt-1 tabular-nums leading-none">{count}</p>
    </div>
  );
};

const emptyForm = () => ({
  title: "", description: "", priority: "Medium",
  due_date: "", type: "General", status: "pending",
});

const TaskForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || emptyForm());
  useEffect(() => { setForm(initial || emptyForm()); }, [initial]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    onSave(form);
  };
  return (
    <form onSubmit={submit} className="bg-white border border-forest/15 rounded-lg p-5 space-y-4" data-testid="task-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Title <span className="text-red-600">*</span></span>
          <input type="text" value={form.title} onChange={update("title")} data-testid="task-field-title"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Description</span>
          <textarea rows={3} value={form.description} onChange={update("description")} data-testid="task-field-description"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none resize-y" />
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Priority</span>
          <select value={form.priority} onChange={update("priority")} data-testid="task-field-priority"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Type</span>
          <select value={form.type} onChange={update("type")} data-testid="task-field-type"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            {TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Due date (DD/MM/YYYY)</span>
          <input type="date" value={form.due_date || ""} onChange={update("due_date")} data-testid="task-field-due"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
          {form.due_date && <p className="text-[11px] text-sage mt-1">{fmtDMY(form.due_date)}</p>}
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Status</span>
          <select value={form.status} onChange={update("status")} data-testid="task-field-status"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            <option value="pending">Pending</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
          </select>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="task-save-btn" className="btn-gold disabled:opacity-50">
          {saving ? "Saving…" : (initial ? "Save changes" : "Add task")}
        </button>
        <button type="button" onClick={onCancel} data-testid="task-cancel-btn" className="btn-ghost-dark">Cancel</button>
      </div>
    </form>
  );
};

const statusLabel = (s) =>
  s === "pending" ? "Pending" : s === "in_progress" ? "In progress" : "Completed";

export const CustomerTasks = ({ customerId, customerName }) => {
  const [data, setData] = useState({ tasks: [], summary: { pending: 0, in_progress: 0, completed: 0, overdue: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | completed | all
  const [editing, setEditing] = useState(null); // null | "new" | task object
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/admin/customers/${customerId}/tasks`, authHeaders())
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Could not load tasks"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [customerId]);

  const filtered = useMemo(() => {
    const rows = data.tasks || [];
    if (filter === "completed") return rows.filter((t) => t.status === "completed");
    if (filter === "active") return rows.filter((t) => t.status !== "completed");
    return rows;
  }, [data.tasks, filter]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/customers/${customerId}/tasks`, payload, authHeaders());
        toast.success("Task added");
      } else {
        await axios.put(`${API}/admin/customers/${customerId}/tasks/${editing.id}`, payload, authHeaders());
        toast.success("Task updated");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save task");
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (t) => {
    const newStatus = t.status === "completed" ? "pending" : "completed";
    try {
      await axios.put(`${API}/admin/customers/${customerId}/tasks/${t.id}`,
        { ...t, status: newStatus }, authHeaders());
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}/tasks/${t.id}`, authHeaders());
      toast.success("Task deleted");
      reload();
    } catch { toast.error("Could not delete"); }
  };

  if (loading) return <p className="text-sage">Loading tasks…</p>;

  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="customer-tasks-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Pending" count={s.pending} icon={Clock} tone="forest" />
        <StatBox label="In progress" count={s.in_progress} icon={ListChecks} tone="gold" />
        <StatBox label="Completed" count={s.completed} icon={CheckCircle2} tone="green" />
        <StatBox label="Overdue" count={s.overdue} icon={AlertTriangle} tone="red" />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {[
            { v: "active", label: "Active" },
            { v: "completed", label: "Completed" },
            { v: "all", label: "All" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilter(f.v)}
              data-testid={`tasks-filter-${f.v}`}
              className={`px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
                filter === f.v
                  ? "bg-forest text-cream border-forest"
                  : "bg-white border-forest/20 text-forest/70 hover:border-forest"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {editing !== "new" && !editing && (
          <button type="button" onClick={() => setEditing("new")} data-testid="tasks-add-btn"
            className="inline-flex items-center gap-2 bg-forest hover:bg-forest-soft text-cream px-4 py-2.5 rounded text-[11px] font-bold tracking-[0.2em] uppercase transition-colors">
            <Plus size={14} strokeWidth={2.2} /> Add task
          </button>
        )}
      </div>

      {(editing === "new" || typeof editing === "object") && editing && (
        <TaskForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-forest/10 rounded-xl p-10 text-center" data-testid="tasks-empty">
          <ClipboardList size={28} strokeWidth={1.4} className="mx-auto text-forest/30 mb-3" />
          <p className="font-serif text-lg text-forest mb-1">
            {filter === "completed" ? "No completed tasks yet" :
             filter === "all" ? `No tasks yet for ${customerName}` : "No active tasks right now"}
          </p>
          <p className="text-[13px] text-sage mb-5">
            {filter === "all" ? "Tasks you add here are only visible to the team." : "Add the first task to get this case moving."}
          </p>
          {!editing && (
            <button type="button" onClick={() => setEditing("new")} data-testid="tasks-empty-add"
              className="btn-gold inline-flex items-center gap-2">
              <Plus size={14} strokeWidth={2.2} /> Add first task
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((t, i) => (
            <div
              key={t.id}
              data-testid={`task-row-${i + 1}`}
              className={`bg-white border rounded-lg p-4 flex gap-3 items-start ${
                t.overdue ? "border-red-200 bg-red-50/40"
                : t.status === "completed" ? "border-emerald-200 bg-emerald-50/30"
                : "border-forest/10"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleDone(t)}
                title={t.status === "completed" ? "Reopen" : "Mark done"}
                data-testid={`task-toggle-${i + 1}`}
                className={`mt-0.5 w-6 h-6 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                  t.status === "completed"
                    ? "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600"
                    : "bg-white border-forest/30 text-transparent hover:border-forest"
                }`}
              >
                <CheckCircle2 size={14} strokeWidth={2.4} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className={`font-semibold text-forest ${t.status === "completed" ? "line-through text-forest/40" : ""}`}>{t.title}</p>
                    {t.description && (
                      <p className={`text-[13px] mt-1 whitespace-pre-wrap ${t.status === "completed" ? "text-forest/40" : "text-sage"}`}>{t.description}</p>
                    )}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded ${priorityStyle(t.priority)}`}>{t.priority}</span>
                      <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded ${typeStyle(t.type)}`}>{t.type}</span>
                      {t.status !== "completed" && (
                        <span className="inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded bg-cream-3 text-forest/70 border-forest/15">{statusLabel(t.status)}</span>
                      )}
                      {t.due_date && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${t.overdue ? "text-red-700" : "text-forest/60"}`}>
                          <Clock size={11} strokeWidth={1.8} />
                          Due {fmtDMY(t.due_date)}
                          {t.overdue && <span className="font-bold">· Overdue</span>}
                        </span>
                      )}
                      {t.status === "completed" && t.completed_at && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
                          <CheckCircle2 size={11} strokeWidth={1.8} />
                          Done {fmtDMY(t.completed_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => toggleDone(t)} data-testid={`task-reopen-${i + 1}`}
                      title={t.status === "completed" ? "Reopen" : "Mark done"}
                      className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded">
                      {t.status === "completed" ? <RotateCcw size={14} strokeWidth={1.8} /> : <CheckCircle2 size={14} strokeWidth={1.8} />}
                    </button>
                    <button type="button" onClick={() => setEditing(t)} data-testid={`task-edit-${i + 1}`}
                      className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded" title="Edit">
                      <Pencil size={14} strokeWidth={1.8} />
                    </button>
                    <button type="button" onClick={() => remove(t)} data-testid={`task-delete-${i + 1}`}
                      className="p-1.5 text-rose-500/80 hover:text-rose-700 hover:bg-rose-50 rounded" title="Delete">
                      <Trash2 size={14} strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
